// Projeto: Haklai Church — Plugin WordPress Modular - Desenvolvido por: Ricardo Sarmento - https://linx.pt

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import api from '../services/api';
import { useTheme } from '../hooks/useTheme';
import LoadingSpinner from '../components/LoadingSpinner';
import Avatar from '../components/Avatar';
import AppLayout from '../components/AppLayout';

// Leaflet
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { initLeafletIcons } from '../utils/leafletSetup';

// Registra componentes do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function CellDetailPage() {
  initLeafletIcons();
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const { theme } = useTheme();
  // Estados para modais e filtros
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showRemoveMemberModal, setShowRemoveMemberModal] = useState(false);
  const [selectedMemberToRemove, setSelectedMemberToRemove] = useState(null);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberCategoryFilter, setMemberCategoryFilter] = useState('');

  console.log('Rendering CellDetailPage', { id, showAddMemberModal });

  // Carrega dados da célula
  const { data: cell, isLoading: loadingCell, error: cellError, refetch: refetchCell } = useQuery({
    queryKey: ['cell', id],
    queryFn: async () => {
      const response = await api.get(`/cells/${id}`);
      return response.data.data;
    },
  });

  // Carrega hierarquia da célula
  const { data: hierarchyData } = useQuery({
    queryKey: ['cell-hierarchy', id],
    queryFn: async () => {
      const response = await api.get(`/cells/${id}/hierarchy`);
      return response.data.data;
    },
    enabled: !!id,
  });

  // Carrega membros da célula
  const { data: membersData, isLoading: loadingMembers } = useQuery({
    queryKey: ['cell-members', id, memberSearch, memberCategoryFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('cell_id', id);
      if (memberSearch) params.append('search', memberSearch);
      if (memberCategoryFilter) params.append('category', memberCategoryFilter);

      const response = await api.get(`/members?${params.toString()}`);
      return response.data.data || [];
    },
    enabled: !!id,
  });

  // Carrega checkpoints da célula
  const { data: checkpointsData, isLoading: loadingCheckpoints } = useQuery({
    queryKey: ['cell-checkpoints', id],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('cell_id', id);
      params.append('per_page', '50'); // Últimos 50 checkpoints

      const response = await api.get(`/checkpoints?${params.toString()}`);
      return response.data.data?.items || [];
    },
    enabled: !!id,
  });

  // Mutation para adicionar membro à célula
  const addMemberMutation = useMutation({
    mutationFn: async (memberId) => {
      return api.post(`/cells/${id}/members`, { member_id: memberId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cell-members', id] });
      queryClient.invalidateQueries({ queryKey: ['cell', id] });
      queryClient.invalidateQueries({ queryKey: ['members'] });
      setShowAddMemberModal(false);
    },
    onError: (error) => {
      alert('Erro ao adicionar membro: ' + (error.response?.data?.message || error.message));
    },
  });

  // Mutation para remover membro da célula
  const removeMemberMutation = useMutation({
    mutationFn: async (memberId) => {
      return api.delete(`/cells/${id}/members/${memberId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cell-members', id] });
      queryClient.invalidateQueries({ queryKey: ['cell', id] });
      queryClient.invalidateQueries({ queryKey: ['members'] });
      setShowRemoveMemberModal(false);
      setSelectedMemberToRemove(null);
    },
    onError: (error) => {
      alert('Erro ao remover membro: ' + (error.response?.data?.message || error.message));
    },
  });

  // Carrega todos os membros para o modal de adicionar
  const { data: allMembersData } = useQuery({
    queryKey: ['all-members-for-cell', id],
    queryFn: async () => {
      const response = await api.get('/members?per_page=1000');
      return response.data.data || [];
    },
    enabled: showAddMemberModal,
  });

  // Formata dia da semana
  const getDayOfWeekLabel = (day) => {
    const days = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
    return day !== null && day >= 0 && day <= 6 ? days[day] : '-';
  };

  // Formata hora
  const formatTime = (time) => {
    if (!time) return '-';
    if (time.match(/^\d{2}:\d{2}$/)) return time;
    return time;
  };

  // Formata data
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  // Label de categoria
  const getCategoryLabel = (category) => {
    const labels = {
      visitor: 'Visitante',
      frequent: 'Frequentador',
      frequent_encounter: 'Frequentador + Encontro',
      member: 'Membro',
    };
    return labels[category] || category;
  };

  // Calcula dados do gráfico de presença (últimos 6 meses)
  const calculateAttendanceChart = () => {
    if (!checkpointsData || checkpointsData.length === 0) {
      return {
        labels: [],
        presentData: [],
        absentData: [],
      };
    }

    // Agrupa por mês
    const monthlyData = {};
    checkpointsData.forEach(checkpoint => {
      if (!checkpoint.checked_at) return;

      const date = new Date(checkpoint.checked_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { present: 0, absent: 0, total: 0 };
      }

      // Assumindo que temos dados de presença no checkpoint
      // Se não tiver, podemos calcular baseado em attendance
      monthlyData[monthKey].total += 1;
    });

    // Últimos 6 meses
    const months = [];
    const presentData = [];
    const absentData = [];

    for (let i = 5; i >= 0; i--) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = date.toLocaleDateString('pt-BR', { month: 'short' });

      months.push(monthLabel);
      const data = monthlyData[monthKey] || { present: 0, absent: 0 };
      presentData.push(data.present);
      absentData.push(data.absent);
    }

    return { labels: months, presentData, absentData };
  };

  const chartData = calculateAttendanceChart();

  // Dados do gráfico
  const attendanceChartData = {
    labels: chartData.labels,
    datasets: [
      {
        label: 'Presentes',
        data: chartData.presentData,
        borderColor: '#36B37E',
        backgroundColor: 'rgba(54, 179, 126, 0.1)',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Ausentes',
        data: chartData.absentData,
        borderColor: '#FF5630',
        backgroundColor: 'rgba(255, 86, 48, 0.1)',
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: theme === 'dark' ? '#f0f6fc' : '#172B4D',
        },
      },
    },
    scales: {
      x: {
        grid: {
          color: theme === 'dark' ? '#30363d' : '#EBECF0',
        },
        ticks: {
          color: theme === 'dark' ? '#8b949e' : '#6B778C',
        },
      },
      y: {
        grid: {
          color: theme === 'dark' ? '#30363d' : '#EBECF0',
        },
        ticks: {
          color: theme === 'dark' ? '#8b949e' : '#6B778C',
        },
      },
    },
  };

  // Membros já na célula (para filtrar do modal)
  const cellMemberIds = (membersData || []).map(m => m.id);
  const availableMembers = (allMembersData || []).filter(m => !cellMemberIds.includes(m.id));

  if (loadingCell) {
    return (
      <AppLayout>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
          <LoadingSpinner size="lg" />
        </div>
      </AppLayout>
    );
  }

  if (cellError) {
    return (
      <AppLayout>
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>Erro ao carregar célula</h2>
          <p>{cellError.message || 'Erro desconhecido'}</p>
          <button className="btn btn-primary" onClick={() => refetchCell()}>
            Tentar novamente
          </button>
          <button className="btn btn-secondary" onClick={() => navigate('/cells')} style={{ marginLeft: '1rem' }}>
            Voltar para lista
          </button>
        </div>
      </AppLayout>
    );
  }

  if (!cell) {
    return (
      <AppLayout>
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>Célula não encontrada</h2>
          <button className="btn btn-primary" onClick={() => navigate('/cells')}>
            Voltar para lista
          </button>
        </div>
      </AppLayout>
    );
  }

  const members = membersData || [];
  const checkpoints = checkpointsData || [];

  return (
    <AppLayout>
      <div className="cell-detail-page" style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2rem' }}>
          <div style={{ flex: 1 }}>
            <button
              className="btn btn-text"
              onClick={() => navigate('/cells')}
              style={{ marginBottom: '1rem' }}
            >
              <span className="material-symbols-outlined">arrow_back</span>
              Voltar
            </button>
            <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
              {cell.name}
            </h1>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem' }}>
              <span className={`badge ${cell.is_active ? 'badge-success' : 'badge-danger'}`}>
                {cell.is_active ? 'Ativa' : 'Inativa'}
              </span>
              {cell.current_members !== undefined && (
                <span className="badge badge-primary">
                  {cell.current_members} {cell.current_members === 1 ? 'membro' : 'membros'}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              className="btn btn-secondary"
              onClick={() => navigate(`/checkpoint?cell_id=${id}`)}
            >
              <span className="material-symbols-outlined" style={{ marginRight: '0.5rem' }}>fact_check</span>
              Criar Checkpoint
            </button>
            <button
              className="btn btn-primary"
              onClick={() => navigate(`/cells/${id}/edit`)}
            >
              <span className="material-symbols-outlined" style={{ marginRight: '0.5rem' }}>edit</span>
              Editar Célula
            </button>
          </div>
        </div>

        {/* Hierarquia Organizacional */}
        {hierarchyData && (hierarchyData.leader_id || hierarchyData.discipulador_id || hierarchyData.pastor_rede_id || hierarchyData.pastor_senior_id || hierarchyData.pastor_supervisor_id) && (
          <div className="card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '600' }}>
                Hierarquia Organizacional
              </h2>
              <button
                className="btn btn-text btn-sm"
                onClick={() => navigate(`/cells/${id}/edit`)}
              >
                <span className="material-symbols-outlined">edit</span>
                Editar
              </button>
            </div>

            {/* Visualização em árvore hierárquica compacta */}
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '1rem',
              padding: '0.5rem 0'
            }}>
              {[
                { label: 'Pastor Supervisor', id: hierarchyData.pastor_supervisor_id, member: hierarchyData.pastor_supervisor },
                { label: 'Pastor Senior', id: hierarchyData.pastor_senior_id, member: hierarchyData.pastor_senior },
                { label: 'Pastor de Rede', id: hierarchyData.pastor_rede_id, member: hierarchyData.pastor_rede },
                { label: 'Discipulador', id: hierarchyData.discipulador_id, member: hierarchyData.discipulador },
                { label: 'Líder da Célula', id: hierarchyData.leader_id, member: hierarchyData.leader }
              ].filter(item => item.id && item.member).map((item, index, arr) => (
                <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  {/* Indicador visual de conexão */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    minWidth: '24px'
                  }}>
                    <span 
                      className="material-symbols-outlined" 
                      style={{ 
                        color: item.label === 'Líder da Célula' ? 'var(--primary)' : 'var(--text-muted)',
                        fontSize: '1.25rem'
                      }}
                    >
                      {item.label === 'Líder da Célula' ? 'cell_tower' : 'person'}
                    </span>
                    {index < arr.length - 1 && (
                      <div style={{
                        width: '2px',
                        height: '16px',
                        backgroundColor: 'var(--border-color)',
                        marginTop: '0.25rem'
                      }} />
                    )}
                  </div>

                  {/* Informações da Função e Nome */}
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ 
                      fontSize: '0.75rem', 
                      textTransform: 'uppercase', 
                      letterSpacing: '0.05em', 
                      color: 'var(--text-muted)',
                      fontWeight: '500'
                    }}>
                      {item.label}
                    </span>
                    <span style={{ 
                      fontWeight: item.label === 'Líder da Célula' ? '700' : '600', 
                      color: 'var(--text-primary)', 
                      fontSize: '0.95rem' 
                    }}>
                      {item.member.full_name}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Mensagem quando não há hierarquia */}
        {hierarchyData && !hierarchyData.leader_id && !hierarchyData.discipulador_id && !hierarchyData.pastor_rede_id && !hierarchyData.pastor_senior_id && !hierarchyData.pastor_supervisor_id && (
          <div className="card" style={{ padding: '1.5rem', marginBottom: '1.5rem', textAlign: 'center' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '3rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
              account_tree
            </span>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.5rem' }}>
              Nenhuma hierarquia definida
            </h3>
            <p style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>
              Defina a hierarquia organizacional desta célula para melhor gestão.
            </p>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => navigate(`/cells/${id}/edit`)}
            >
              <span className="material-symbols-outlined">add</span>
              Definir Hierarquia
            </button>
          </div>
        )}

        {/* Informações da Célula */}
        <div className="card" style={{ padding: '2rem', marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem' }}>Informações da Célula</h2>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
            {/* Informações Básicas */}
            <div>
              <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--text-secondary)' }}>
                Informações Básicas
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {cell.leader && (
                  <div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Líder da Célula</div>
                    <div style={{ fontWeight: '500' }}>{cell.leader.full_name}</div>
                  </div>
                )}
                {cell.description && (
                  <div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Descrição</div>
                    <div>{cell.description}</div>
                  </div>
                )}
                {(cell.day_of_week !== null || cell.start_time) && (
                  <div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Horário</div>
                    <div>
                      {getDayOfWeekLabel(cell.day_of_week)} {cell.start_time && `às ${formatTime(cell.start_time)}`}
                    </div>
                  </div>
                )}
                {cell.max_capacity && (
                  <div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Capacidade Máxima</div>
                    <div>{cell.max_capacity} pessoas</div>
                  </div>
                )}
              </div>
            </div>

            {/* Localização */}
            <div>
              <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--text-secondary)' }}>
                Localização
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {cell.address && (
                  <div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Endereço</div>
                    <div>{cell.address}</div>
                  </div>
                )}
                {cell.neighborhood && (
                  <div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Bairro</div>
                    <div>{cell.neighborhood}</div>
                  </div>
                )}
                {(cell.latitude && cell.longitude) ? (
                  <div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>Coordenadas</div>
                    <div style={{
                      width: '100%',
                      height: '250px',
                      borderRadius: 'var(--radius-lg)',
                      overflow: 'hidden',
                      border: '1px solid var(--border)',
                      position: 'relative'
                    }}>
                      <MapContainer
                        center={[parseFloat(cell.latitude), parseFloat(cell.longitude)]}
                        zoom={15}
                        style={{ height: '100%', width: '100%', zIndex: 1, position: 'absolute', top: 0, left: 0 }}
                        scrollWheelZoom={false}
                      >
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <Marker position={[parseFloat(cell.latitude), parseFloat(cell.longitude)]}>
                          <Popup>
                            <strong>{cell.name}</strong><br />
                            {cell.address}
                          </Popup>
                        </Marker>
                      </MapContainer>
                    </div>
                  </div>
                ) : (
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                    Localização não definida
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Grid Principal: Membros e Gráfico */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '2rem', marginBottom: '2rem' }}>
          {/* Lista de Membros */}
          <div className="card" style={{ padding: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '600' }}>Membros ({members.length})</h2>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Clicou no botão Adicionar!');
                  setShowAddMemberModal(true);
                }}
              >
                <span className="material-symbols-outlined" style={{ marginRight: '0.5rem' }}>person_add</span>
                Adicionar
              </button>
            </div>

            {/* Filtros de Membros */}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
              <input
                type="text"
                className="form-input"
                placeholder="Buscar membro..."
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                style={{ flex: 1, minWidth: '200px' }}
              />
              <select
                className="form-input"
                value={memberCategoryFilter}
                onChange={(e) => setMemberCategoryFilter(e.target.value)}
                style={{ width: '200px' }}
              >
                <option value="">Todas categorias</option>
                <option value="visitor">Visitante</option>
                <option value="frequent">Frequentador</option>
                <option value="frequent_encounter">Frequentador + Encontro</option>
                <option value="member">Membro</option>
              </select>
            </div>

            {/* Grid de Membros */}
            {loadingMembers ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                <LoadingSpinner />
              </div>
            ) : members.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '3rem', marginBottom: '1rem' }}>person_off</span>
                <div>Nenhum membro associado</div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '1rem' }}>
                {members.map(member => (
                  <div
                    key={member.id}
                    className="card"
                    style={{
                      padding: '1rem',
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    onClick={() => navigate(`/members/${member.id}`)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
                    }}
                  >
                    <Avatar
                      name={member.full_name}
                      photo={member.photo_thumbnail_url}
                      size="lg"
                      style={{ margin: '0 auto 0.5rem' }}
                    />
                    <div style={{ fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.25rem' }}>
                      {member.full_name}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                      {getCategoryLabel(member.category)}
                    </div>
                    <button
                      className="btn btn-text btn-sm btn-danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedMemberToRemove(member);
                        setShowRemoveMemberModal(true);
                      }}
                      style={{ width: '100%' }}
                    >
                      Remover
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Gráfico de Presença */}
          <div className="card" style={{ padding: '2rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem' }}>
              Presença (Últimos 6 Meses)
            </h2>
            {chartData.labels.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '3rem', marginBottom: '1rem' }}>bar_chart</span>
                <div>Nenhum dado de presença disponível</div>
              </div>
            ) : (
              <div style={{ height: '300px' }}>
                <Line data={attendanceChartData} options={chartOptions} />
              </div>
            )}
          </div>
        </div>

        {/* Histórico de Checkpoints */}
        <div className="card" style={{ padding: '2rem' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem' }}>
            Histórico de Checkpoints ({checkpoints.length})
          </h2>
          {loadingCheckpoints ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
              <LoadingSpinner />
            </div>
          ) : checkpoints.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
              <span className="material-symbols-outlined" style={{ fontSize: '3rem', marginBottom: '1rem' }}>event_busy</span>
              <div>Nenhum checkpoint registrado</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)' }}>
                      Data
                    </th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)' }}>
                      Evento
                    </th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)' }}>
                      Presentes
                    </th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)' }}>
                      Ausentes
                    </th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)' }}>
                      Visitantes
                    </th>
                    <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary)' }}>
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {checkpoints.map(checkpoint => (
                    <tr key={checkpoint.id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                      <td style={{ padding: '0.75rem', color: 'var(--text-primary)' }}>
                        {formatDate(checkpoint.checked_at)}
                      </td>
                      <td style={{ padding: '0.75rem', color: 'var(--text-secondary)' }}>
                        {checkpoint.event?.name || '-'}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                        {checkpoint.total_present}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                        {checkpoint.total_absent}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                        {checkpoint.total_visitors}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                        <button
                          className="btn btn-text btn-sm"
                          onClick={() => navigate(`/checkpoint?checkpoint_id=${checkpoint.id}`)}
                        >
                          Ver Detalhes
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modal: Adicionar Membro */}
        {showAddMemberModal && (
          <div
            className="modal-overlay active"
            onClick={() => setShowAddMemberModal(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
            }}
          >
            <div
              className="card"
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: '600px',
                width: '90%',
                maxHeight: '80vh',
                overflow: 'auto',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: '600' }}>Adicionar Membro à Célula</h2>
                <button
                  className="btn btn-text"
                  onClick={() => setShowAddMemberModal(false)}
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>

              {availableMembers.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                  {allMembersData && allMembersData.length === 0 ? (
                    <>
                      <span className="material-symbols-outlined" style={{ fontSize: '3rem', marginBottom: '1rem', display: 'block' }}>person_add</span>
                      <div style={{ marginBottom: '1rem', fontWeight: '500', fontSize: '1rem' }}>Nenhum membro cadastrado</div>
                      <p style={{ marginBottom: '1.5rem', fontSize: '0.875rem' }}>
                        Você precisa cadastrar membros antes de adicioná-los à célula.
                      </p>
                      <button
                        className="btn btn-primary"
                        onClick={() => {
                          setShowAddMemberModal(false);
                          navigate('/members/new');
                        }}
                      >
                        <span className="material-symbols-outlined" style={{ marginRight: '0.5rem' }}>add</span>
                        Criar Primeiro Membro
                      </button>
                    </>
                  ) : (
                    <div>Todos os membros já estão nesta célula</div>
                  )}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {availableMembers.map(member => (
                    <div
                      key={member.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        padding: '1rem',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-lg)',
                        cursor: 'pointer',
                      }}
                      onClick={() => addMemberMutation.mutate(member.id)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--surface-hover)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <Avatar name={member.full_name} photo={member.photo_thumbnail_url} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '500' }}>{member.full_name}</div>
                        <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                          {member.email || '-'}
                        </div>
                      </div>
                      <span className="material-symbols-outlined">add</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modal: Remover Membro */}
        {showRemoveMemberModal && selectedMemberToRemove && (
          <div
            className="modal-overlay active"
            onClick={() => {
              setShowRemoveMemberModal(false);
              setSelectedMemberToRemove(null);
            }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
            }}
          >
            <div
              className="card"
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: '400px',
                width: '90%',
              }}
            >
              <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>
                Remover Membro
              </h2>
              <p style={{ marginBottom: '1.5rem', color: 'var(--text-secondary)' }}>
                Tem certeza que deseja remover <strong>{selectedMemberToRemove.full_name}</strong> desta célula?
              </p>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowRemoveMemberModal(false);
                    setSelectedMemberToRemove(null);
                  }}
                >
                  Cancelar
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => removeMemberMutation.mutate(selectedMemberToRemove.id)}
                  disabled={removeMemberMutation.isPending}
                >
                  {removeMemberMutation.isPending ? 'Removendo...' : 'Remover'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}

export default CellDetailPage;
